/**
 * User Guide Component
 *
 * Comprehensive guide for new users covering:
 * - Getting Started (login, Claude setup)
 * - Chat Interface
 * - File Browser
 * - Skills (auto-invoked, list from marketplace)
 * - Settings
 * - Admin Features (admin only)
 *
 * Screenshots will be added to public/guide/ folder.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  Rocket,
  MessageSquare,
  FolderOpen,
  Sparkles,
  Settings,
  Shield,
  ChevronRight,
  HelpCircle,
  Link2,
} from 'lucide-react';

// ============================================
// Types
// ============================================

type GuideSection = 'getting-started' | 'connecting-claude' | 'chat' | 'files' | 'skills' | 'settings' | 'admin';

interface SharedSkill {
  name: string;
  title: string;
  description: string;
}

// ============================================
// Section Components
// ============================================

function GettingStartedSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Getting Started
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          Welcome to TelcoOS! This guide will help you get up and running quickly.
        </p>
      </div>

      {/* Step 1: Login */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Log In
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Use the credentials provided by your administrator to log in.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/login.png" alt="Login page" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Login page (add login.png to /guide/)
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          What's Next?
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          After logging in, you'll need to connect your Claude account. Head to the <strong>Connecting Claude</strong> section for detailed instructions.
        </p>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-blue-700 dark:text-blue-300">
            <strong>First time?</strong> Make sure you have an active Claude subscription (Pro, Max, Team, or Enterprise) before connecting.
          </p>
        </div>
      </div>
    </div>
  );
}

function ConnectingClaudeSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Connecting Claude
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          To use TelcoOS, you need to connect your Claude account. This is a one-time setup that takes about 2 minutes.
        </p>
      </div>

      {/* Step 1: Open Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 1: Open Settings
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Click the <strong>Settings</strong> icon (gear) in the top right corner of the screen.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step1-settings.png" alt="Open Settings" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Settings icon location (add claude-step1-settings.png)
          </div>
        </div>
      </div>

      {/* Step 2: Go to AI Connections */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 2: Go to AI Connections
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          In the Settings panel, click <strong>AI Connections</strong> in the left sidebar, then click <strong>Connect Claude Account</strong>.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step2-integrations.png" alt="AI Connections panel" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: AI Connections panel (add claude-step2-integrations.png)
          </div>
        </div>
      </div>

      {/* Step 3: Run Login Command */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 3: Run Login Command
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          A terminal will appear. Type <code className="px-1.5 py-0.5 bg-surface-200 dark:bg-surface-700 rounded text-sm">claude login</code> and press <strong>Enter</strong>.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step3-login.png" alt="Run claude login" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Terminal with claude login (add claude-step3-login.png)
          </div>
        </div>
      </div>

      {/* Step 4: Skip Theme */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 4: Skip Theme Selection
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Choose the default setting. TelcoOS has its own theme settings.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step4-theme.png" alt="Skip theme" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Theme selection (add claude-step5-theme.png)
          </div>
        </div>
      </div>

      {/* Step 5: Select Account Type */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 5: Select Account Type
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Select <strong>1</strong> for "Claude.ai account" (requires a Pro, Max, Team, or Enterprise subscription).
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step5-account.png" alt="Select account type" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Account type selection (add claude-step6-account.png)
          </div>
        </div>
      </div>

      {/* Step 6: Copy URL */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 6: Copy the URL
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          A URL will appear in the terminal. <strong>Copy the entire URL</strong> (make sure not to select any extra spaces). Open a new tab in your browser and paste the entire URL into the address bar.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step6-url.png" alt="Copy URL" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: URL to copy (add claude-step7-url.png)
          </div>
        </div>
      </div>

      {/* Step 7: Authorize */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 7: Authorize Your Account
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Authorize your account to use TelcoOS. After authorizing, <strong>copy the token</strong> that is shown.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
            <img src="/guide/claude-step7-authorize-1.png" alt="Authorize account" className="rounded-lg w-full" onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }} />
            <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
              Screenshot: Authorize (add claude-step7-authorize-1.png)
            </div>
          </div>
          <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
            <img src="/guide/claude-step-7-2.png" alt="Copy token" className="rounded-lg w-full" onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }} />
            <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
              Screenshot: Copy token (add claude-step-7-2.png)
            </div>
          </div>
        </div>
      </div>

      {/* Step 8: Paste Token */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 8: Paste the Token
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Paste the entire token back into the terminal where it asks you to, then press <strong>Enter</strong>.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step8-paste-token.png" alt="Paste token" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Paste token (add claude-step8-paste-token.png)
          </div>
        </div>
      </div>

      {/* Step 9: Success */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 9: Success!
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          You'll see a success message confirming your account is connected.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step9-success.png" alt="Success" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Success (add claude-step7-success.png)
          </div>
        </div>
      </div>

      {/* Step 10: Accept Defaults */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Step 10: Accept Defaults
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Press <strong>Enter</strong> 3 times to accept the default settings. Close the terminal and you're ready to start chatting!
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/claude-step10-defaults.png" alt="Accept defaults" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Accept defaults (add claude-step10-defaults.png)
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Chat Interface
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          The chat is your main way to interact with Claude. Just type naturally - Claude understands context and can help with telecom data analysis, writing emails, generating reports, and more.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Starting a Conversation
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Type your message in the input box at the bottom and press Enter. Claude will respond in the chat area above.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/chat-interface.png" alt="Chat interface" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: Chat interface (add chat-interface.png)
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Managing Chat Sessions
        </h3>
        <ul className="space-y-2 text-surface-600 dark:text-surface-400">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>New Chat:</strong> Click "New Chat" to start a fresh conversation</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Rename:</strong> Double-click a chat name to rename it</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Delete:</strong> Click the trash icon, then click again to confirm</span>
          </li>
        </ul>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-blue-700 dark:text-blue-300">
          <strong>Pro Tip:</strong> Be specific in your requests. Instead of "analyze this data", try "compare the rates in the uploaded CSV and highlight any that are above market average."
        </p>
      </div>
    </div>
  );
}

function FilesSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          File Browser
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          Upload files and access your outputs through the file browser in the left sidebar.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Uploading Files
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Click the upload button or drag and drop files into the Inbox folder. The chat agent can then analyze, process, or reference these files.
        </p>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/file-browser.png" alt="File browser" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: File browser (add file-browser.png)
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Finding Your Outputs
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          When a chat run generates reports, rate decks, or other files, they're saved to the <strong>Output</strong> folder unless you or a selected skill choose a more specific destination.
        </p>
        <ul className="space-y-2 text-surface-600 dark:text-surface-400">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Reports:</strong> Output/</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Rate Decks:</strong> Output/Rates/</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Emails:</strong> Output/Emails/</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function SkillsSection({ skills }: { skills: SharedSkill[] }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Skills
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          Skills are specialized capabilities that Claude can use to help you. The best part? <strong>You don't need to invoke them explicitly.</strong> Just describe what you need, and Claude will automatically use the right skill.
        </p>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <p className="text-amber-700 dark:text-amber-300">
          <strong>How it works:</strong> Instead of typing a command like "/email", just say "Write an email to John about the new rates" and Claude will automatically use the email skill.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Available Skills
        </h3>

        {skills.length === 0 ? (
          <p className="text-surface-500 dark:text-surface-400 italic">
            Loading skills...
          </p>
        ) : (
          <div className="space-y-3">
            {skills.map((skill) => (
              <div
                key={skill.name}
                className="p-4 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50"
              >
                <h4 className="font-medium text-surface-900 dark:text-white">
                  {skill.title || skill.name}
                </h4>
                <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                  {skill.description || 'No description available'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Example Requests
        </h3>
        <div className="space-y-2">
          <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-lg">
            <p className="text-surface-700 dark:text-surface-300 italic">"Generate a rate deck for Acme Corp using LCR1 with 15% margin"</p>
          </div>
          <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-lg">
            <p className="text-surface-700 dark:text-surface-300 italic">"Write an email to the billing team about the invoice discrepancy"</p>
          </div>
          <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-lg">
            <p className="text-surface-700 dark:text-surface-300 italic">"Summarize the uploaded CDR report and highlight any issues"</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Settings
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          Customize your experience through the Settings panel. Click the gear icon in the top right.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          App Settings
        </h3>
        <ul className="space-y-2 text-surface-600 dark:text-surface-400">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Your Name:</strong> Set the name Claude uses to greet you</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Theme:</strong> Choose Light, Dark, or System</span>
          </li>
        </ul>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          AI Connections
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          Connect provider accounts.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Security
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          View security information and understand what stays on the server and what can be sent to a connected assistant provider during a chat.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Change Your Password
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          In App Settings, expand "Change Password" to update your login credentials.
        </p>
      </div>
    </div>
  );
}

function AdminSection() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">
          Admin Features
        </h2>
        <p className="text-surface-600 dark:text-surface-400">
          As an admin, you have access to additional features for managing users and skills.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          User Management
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          In Settings → Users, you can:
        </p>
        <ul className="space-y-2 text-surface-600 dark:text-surface-400">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Create Users:</strong> Add new user accounts</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Reset Passwords:</strong> Help users who forgot their password</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Reset Claude Auth:</strong> Clear a user's Claude connection if needed</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-1 text-primary-500" />
            <span><strong>Delete Users:</strong> Remove user accounts</span>
          </li>
        </ul>
        <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
          <img src="/guide/user-management.png" alt="User management" className="rounded-lg w-full" onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }} />
          <div className="hidden h-48 flex items-center justify-center text-surface-400 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded">
            Screenshot: User management (add user-management.png)
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
          Skills Management
        </h3>
        <p className="text-surface-600 dark:text-surface-400">
          In Settings → Skills, you can upload and manage shared skills that are available to all users.
        </p>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function UserGuide() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.isAdmin || false;

  const [activeSection, setActiveSection] = useState<GuideSection>('getting-started');
  const [skills, setSkills] = useState<SharedSkill[]>([]);

  // Fetch skills for the Skills section
  useEffect(() => {
    async function fetchSkills() {
      try {
        const response = await api.skills.listShared();
        // Filter out internal skills (starting with _)
        const publicSkills = response.skills.filter((s: SharedSkill) => !s.name.startsWith('_'));
        setSkills(publicSkills);
      } catch (err) {
        console.error('Failed to load skills:', err);
      }
    }
    fetchSkills();
  }, []);

  const sections: { id: GuideSection; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { id: 'getting-started', label: 'Getting Started', icon: <Rocket className="w-4 h-4" /> },
    { id: 'connecting-claude', label: 'Connecting Claude', icon: <Link2 className="w-4 h-4" /> },
    { id: 'chat', label: 'Chat Interface', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'files', label: 'File Browser', icon: <FolderOpen className="w-4 h-4" /> },
    { id: 'skills', label: 'Skills', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
    { id: 'admin', label: 'Admin Features', icon: <Shield className="w-4 h-4" />, adminOnly: true },
  ];

  const visibleSections = sections.filter(s => !s.adminOnly || isAdmin);

  const renderSection = () => {
    switch (activeSection) {
      case 'getting-started':
        return <GettingStartedSection />;
      case 'connecting-claude':
        return <ConnectingClaudeSection />;
      case 'chat':
        return <ChatSection />;
      case 'files':
        return <FilesSection />;
      case 'skills':
        return <SkillsSection skills={skills} />;
      case 'settings':
        return <SettingsSection />;
      case 'admin':
        return <AdminSection />;
      default:
        return <GettingStartedSection />;
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to App
            </Button>
            <div className="h-6 w-px bg-surface-200 dark:bg-surface-700" />
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary-500" />
              <h1 className="font-semibold text-surface-900 dark:text-white">User Guide</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <nav className="w-56 shrink-0">
            <div className="sticky top-24 space-y-1">
              {visibleSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                    activeSection === section.id
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                      : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
                  )}
                >
                  {section.icon}
                  {section.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 p-8">
              {renderSection()}
            </div>

          </main>
        </div>
      </div>
    </div>
  );
}

export default UserGuide;
